import React, { useState } from "react"
import { X, MapPin, Crosshair, Loader2, CheckCircle2 } from "lucide-react"
import { motion } from "framer-motion"
import { MapView } from "./MapView"
import { GPSModalProps, PickedLocation } from "./types"

export const ModalContent: React.FC<GPSModalProps> = ({
  onClose, onConfirm, initialLat, initialLng,
}) => {
  const [picked, setPicked] = useState<PickedLocation | null>(
    initialLat && initialLng
      ? { lat: parseFloat(initialLat), lng: parseFloat(initialLng) }
      : null
  )
  const [gpsLoading, setGpsLoading] = useState(false)
  const [gpsLocation, setGpsLocation] = useState<PickedLocation | null>(null)
  const [accuracy, setAccuracy] = useState<number | null>(null)
  const [flyTarget, setFlyTarget] = useState<PickedLocation | null>(null)

  const useCurrentLocation = () => {
    setGpsLoading(true)
    setGpsLocation(null)
    setAccuracy(null)

    navigator.geolocation?.getCurrentPosition(
      pos => {
        const { latitude: lat, longitude: lng, accuracy: acc } = pos.coords
        setGpsLocation({ lat, lng })
        setAccuracy(acc)
        setPicked({ lat, lng })
        setFlyTarget({ lat, lng })
        setGpsLoading(false)
      },
      () => {
        const lat = -1.286389
        const lng = 36.817223
        setGpsLocation({ lat, lng })
        setAccuracy(50)
        setPicked({ lat, lng })
        setFlyTarget({ lat, lng })
        setGpsLoading(false)
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    )
  }

  const handleConfirm = () => {
    if (!picked) return
    onConfirm(picked.lat.toFixed(6), picked.lng.toFixed(6))
    onClose()
  }

  const handleMapPick = (lat: number, lng: number) => {
    setPicked({ lat, lng })
    setGpsLocation(null)
    setFlyTarget(null)
  }

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9998 }}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{
          position: "fixed", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "16px", zIndex: 9999, pointerEvents: "none",
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ pointerEvents: "auto", width: "100%", maxWidth: "700px" }}
          className="bg-[#161616] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <MapPin className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Pin Farm Location</h3>
                <p className="text-[10px] text-neutral-500">
                  Detect GPS or click anywhere on the map to place a pin
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose}
              className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* GPS bar */}
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3 flex-wrap">
            <button type="button" onClick={useCurrentLocation} disabled={gpsLoading}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-semibold rounded-lg transition-all disabled:opacity-60"
            >
              {gpsLoading
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Crosshair className="w-3.5 h-3.5" />
              }
              {gpsLoading ? "Detecting your location..." : "Use My Current Location"}
            </button>

            {accuracy !== null && !gpsLoading && (
              <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-[10px] text-neutral-400">
                  Accuracy: ±{Math.round(accuracy)}m
                </span>
              </div>
            )}

            {!gpsLoading && !gpsLocation && (
              <span className="text-neutral-600 text-xs">or click on the map</span>
            )}
          </div>

          {/* Map */}
          <MapView
            picked={picked}
            gpsLocation={gpsLocation}
            gpsLoading={gpsLoading}
            accuracy={accuracy}
            flyTarget={flyTarget}
            onPick={handleMapPick}
          />

          {/* Footer */}
          <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              {gpsLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 text-emerald-400 animate-spin flex-shrink-0" />
                  <span className="text-neutral-500 text-xs">Waiting for GPS signal...</span>
                </div>
              ) : picked ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  <span className="text-emerald-400 text-xs font-mono truncate">
                    {picked.lat.toFixed(6)}, {picked.lng.toFixed(6)}
                  </span>
                </div>
              ) : (
                <span className="text-neutral-600 text-xs">No location selected yet</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-xs font-medium text-neutral-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button type="button" onClick={handleConfirm} disabled={!picked || gpsLoading}
                className="px-5 py-2 text-xs font-bold text-black bg-emerald-500 hover:bg-emerald-400 disabled:opacity-30 rounded-lg transition-all flex items-center gap-1.5"
              >
                <MapPin className="w-3.5 h-3.5" />
                Confirm Location
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  )
}